<script setup lang="ts">
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import CommunityBottomSheet from '@/components/common/CommunityBottomSheet.vue'
import {
  UIDivider,
  UIFormItem,
  UIInput,
  UITextLgMedium,
  UITextMdMedium,
  UITextSmRegular,
} from '@gohighlevel/ghl-ui'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const openDescriptionModal = ref(false)
const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
  updateFormData: {
    type: Function,
    required: true,
  },
  handleGoLive: {
    type: Function,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const { t } = useI18n()
const description = ref(props.formData.description)
const title = ref(props.formData.title)

const isValid = computed(() => {
  return title.value && description.value
})

const savePostDetails = () => {
  props.updateFormData('description', description.value)
  props.updateFormData('title', title.value)
  openDescriptionModal.value = false
}

const handleGoLive = () => {
  props.handleGoLive()
}

const openDescription = () => {
  openDescriptionModal.value = true
}
onMounted(() => {
  openDescriptionModal.value = true
})
</script>

<template>
  <div
    class="bg-communities-background/90 absolute bottom-10 z-10 z-40 flex w-full"
  >
    <div class="w-full flex-col items-center justify-center">
      <div
        class="mx-2 mb-2 text-white"
        tabindex="0"
        role="button"
        @click="openDescription"
        @keyup.enter="openDescription"
      >
        <div
          v-if="formData.description && formData.title"
          class="flex flex-col text-white"
        >
          <UITextMdMedium
            class="mb-2 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ formData.title }}
          </UITextMdMedium>
          <UITextSmRegular class="line-clamp-3">
            {{ formData.description }}
          </UITextSmRegular>
        </div>
        <div v-else>{{ t('golive.tapToAddDescription') }}</div>
      </div>
      <div class="flex w-full justify-center px-2">
        <CommunityButton
          :id="`go-live-btn`"
          type="primary"
          size="medium"
          class="golive-btn w-[100%] normal-case"
          :disabled="!isValid"
          :loading="isLoading"
          @click="handleGoLive"
        >
          {{ t('golive.golive') }}
        </CommunityButton>
      </div>
    </div>
  </div>
  <CommunityBottomSheet
    v-model="openDescriptionModal"
    :is-full-screen="false"
    :is-drag-handle="true"
    :is-scroll-lock="true"
    :snap-point="'400px'"
    :is-backdrop="true"
  >
    <div class="px-2 pb-4 text-communities-font-primary">
      <UITextLgMedium
        class="fon mb-1 text-center text-xl text-communities-font-primary"
      >
        {{ t('golive.addPostDetails') }}
      </UITextLgMedium>
      <UIDivider />
      <UIFormItem :label="t('common.title')" path="title" class="text-lg">
        <UIInput
          id="post-title"
          v-model="title"
          :placeholder="t('golive.enterTitle')"
          :show-count="true"
          :maxlength="100"
        />
      </UIFormItem>
      <UIFormItem :label="t('common.description')" path="description">
        <UIInput
          id="post-description"
          v-model="description"
          :placeholder="t('common.enterDescription')"
          type="textarea"
        />
      </UIFormItem>
      <CommunityButton
        :id="`save-post-details-btn`"
        type="primary"
        size="medium"
        class="w-full"
        :disabled="!isValid"
        @click="savePostDetails"
      >
        {{ t('common.done') }}
      </CommunityButton>
    </div>
  </CommunityBottomSheet>
</template>

<style scoped>
.n-divider {
  margin: 0px !important;
  margin-bottom: 24px !important;
}
.n-form-item-label .n-form-item-label__text {
  font-size: 16px !important;
}
.golive-btn {
  background-color: white !important;
  color: #344054 !important;
}
</style>
